import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import PropTypes from "prop-types";
import { Button, SvgIconHandler } from "../../atoms";
import { STATIC_KEYWORDS } from "../../../constants";
import { SocialShareTemplate } from "../../../arrow";
import { SocialShare } from "@quintype/components";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { TableView } from "./table-view";
import { YearDropdown } from "../mpw-detail-page/year-drop-down";
import { SortDropdown } from "./sort-dropdown";
import SearchIcon from "./search";
import RankingColumnsTheNext500 from "./ranking-columns-thenext500";
import RankingColumnsFortune500 from "./ranking-columns-fortune500";
import RankingColumnsEmergingCompanies from "./ranking-columns-emerging-companies";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { trackLoadMoreClick } from "../../utils";

import "./ranking-table.m.css";

export const RankingsTable = ({ data }) => {
  const {
    sortedEntitiesByYearJson,
    snapshotByRank,
    rankingEntitySlug,
    entitiesByYearJson,
    sortedYears,
    year,
    staticData,
    footnotes,
  } = data;
  const { detailPageTitle, rankingDescription } = sortedYears.find((obj) => obj.yearObj.slug === year);
  let RankingColumnsFiels = RankingColumnsFortune500;
  if (rankingEntitySlug == "the-emerging-companies") {
    RankingColumnsFiels = RankingColumnsEmergingCompanies;
  } else if (rankingEntitySlug == "the-next-500") {
    RankingColumnsFiels = RankingColumnsTheNext500;
  }

  if (rankingEntitySlug === "fortune-500") {
    RankingColumnsFiels = RankingColumnsFiels.filter((col) => !col.exclude_years?.includes(Number(year)));
  }

  const [columns, setColumns] = useState(RankingColumnsFiels);
  const { label, top15Label } = staticData;

  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";

  const { LOAD_MORE, READ_MORE, NO_RESULT_FOUND, TRY_AGAIN } = STATIC_KEYWORDS;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const restoreColumns = columns.filter((i) => !i.show);
  const [enableLoadMore, setEnableLoadMore] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [readMoreClicked, setReadMoreClicked] = useState(false);
  const [yearDropdownActive, setYearDropdownActive] = useState(false);
  const [activeYear, setActiveYear] = useState(year);

  const eventActions = {
    "fortune-500": "company_select",
    "the-next-500": "company_select",
    "the-emerging-companies": "company_select",
    "most-powerful-women": "profile_select",
    "40-under-40": "profile_select",
  };

  let isTracking = false;
  const trackRankingInteraction = (action, value, position = null) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_ranking_detail_interactions",
        event_action: action,
        link_text: value,
        link_position: position,
        category: "rankings",
        sub_category: data?.rankingEntitySlug || "NA",
        template_name: "rankdetail",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const [visibleItemsForList, setVisibleItemsForList] = useState(1000);
  const [showEntities, setShowEntities] = useState(snapshotByRank);
  const [searchCount, setSearchCount] = useState(snapshotByRank.length);
  const [fullUrl, setFullUrl] = useState("");

  const subheadlineRef = useRef(null);
  const searchInputRef = useRef(null);

  const expandBio = () => setReadMoreClicked(true);

  const handleSearch = (e) => {
    e.preventDefault();
    const searchTerm = searchInputRef.current.value;
    setSearchText(searchTerm);
    if (searchTerm) {
      trackRankingInteraction("ranking_search", searchTerm.toLowerCase());
    }
  };

  const getMoreStories = () => {
    trackLoadMoreClick({
      linkCategory: `${rankingEntitySlug}-${year}`,
      category: "ranking",
      subCategory: rankingEntitySlug,
      templateName: "rankdetail",
    });
    setVisibleItemsForList((prev) => prev + visibleItemsForList);
  };

  const handleSort = (column) => {
    trackRankingInteraction("sort_applied", column?.name?.toLowerCase());
    const sortedEntities = [...snapshotByRank].sort((a, b) => {
      const aValue = isNaN(parseFloat(a[column.value])) ? -Infinity : parseFloat(a[column.value]);
      const bValue = isNaN(parseFloat(b[column.value])) ? -Infinity : parseFloat(b[column.value]);

      if (aValue > bValue) return column.name.toLowerCase() === "overall" ? 1 : -1;
      if (aValue < bValue) return column.name.toLowerCase() === "overall" ? -1 : 1;
    });
    setShowEntities(sortedEntities);
  };

  const handleRestoreColumn = (c) => () => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => (column.value === c.value ? { ...column, show: !column.show } : column))
    );
  };

  const includesCaseInsensitive = (str = "", searchTerm = "") =>
    String(str || "")
      .toLowerCase()
      .includes(
        String(searchTerm || "")
          .trim()
          .toLowerCase()
      );

  useEffect(() => {
    if (typeof window !== "undefined") {
      setFullUrl(window.location.href);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_page_info",
        cms_author_name: "NA",
        byline: "NA",
        category: "rankings",
        sub_category:
          data?.rankingEntitySlug?.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) || "NA",
        content_id: "NA",
        template_name: "rankdetail",
        publish_date: "NA",
        publish_time: "NA",
        login_status: "not-logged-in", // will be changed
        subscription_status: "not-subscribed", // will be changed
        ad_free: false, // will be changed
        login_wall_enabled: false,
        platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
      });
    }
    setVisibleItemsForList(10);
  }, []);

  useEffect(() => {
    setEnableLoadMore(visibleItemsForList < snapshotByRank.length);
  }, [visibleItemsForList]);

  useEffect(() => {
    setTimeout(() => {
      const urlPath = new URL(window.location.href).pathname.split("/");
      const activeYear = urlPath[urlPath.length - 1];
      setActiveYear(activeYear);
    }, 100);
  }, []);

  useEffect(() => {
    setEnableLoadMore(snapshotByRank.length > visibleItemsForList);
  }, [visibleItemsForList, snapshotByRank.length]);

  useEffect(() => {
    if (!yearDropdownActive || isDesktop) {
      document.body.classList.remove("no-scroll");
    }
  }, [yearDropdownActive]);

  useEffect(() => {
    if (!isDesktop && subheadlineRef.current) {
      const bioHeight = window?.getComputedStyle(subheadlineRef.current);
      const getLineHeight = parseFloat(bioHeight.lineHeight);
      const lineHeightForThree = getLineHeight * 3;
      const contentHeight = subheadlineRef.current.scrollHeight;
      subheadlineRef.current.style.height = `${lineHeightForThree}px`;
      subheadlineRef.current.style.overflow = "hidden";

      if (readMoreClicked) {
        subheadlineRef.current.style.height = `${contentHeight}px`;
      }
    }
  }, [readMoreClicked, isDesktop]);

  useEffect(() => {
    let entities = snapshotByRank || [];
    if (searchText) {
      entities = entities.filter((entity) => includesCaseInsensitive(entity.name, searchText));
    }
    setSearchCount(entities.length);
    setShowEntities(entities);
  }, [snapshotByRank, searchText]);

  const logoName = {
    "fortune-500": !isDesktop ? "/images/banners/Fortune500Mobile.png" : "/images/banners/Fortune500Desktop.png",
    "the-next-500": !isDesktop ? "/images/banners/Next500Mobile.png" : "/images/banners/Next500Desktop.png",
    "the-emerging-companies": !isDesktop
      ? "/images/banners/EmergingCompaniesMobile.png"
      : "/images/banners/EmergingCompaniesDesktop.png",
  };

  return (
    <div styleName="entity-detail-page">
      <div
        styleName="banner">
        <img src={logoName[data.rankingEntitySlug]} alt={detailPageTitle} title={detailPageTitle} loading="lazy" width={1470} height={417} />
      </div>
      <div className="container" styleName="content-wrapper">
        <div styleName="header">
          <div styleName="header-content">
            <div styleName="headline">
              <span styleName="yeardrop">
                <YearDropdown
                  isDesktop={isDesktop}
                  yearDropdownActive={yearDropdownActive}
                  setYearDropdownActive={setYearDropdownActive}
                  yearData={sortedEntitiesByYearJson}
                  activeYear={activeYear}
                  setActiveYear={setActiveYear}
                  rankingEntitySlug={rankingEntitySlug}
                  entitiesByYearJson={entitiesByYearJson}
                  onYearChange={(year) => trackRankingInteraction("year_filter_applied", year)}
                />
              </span>
              <h1 styleName="text">{`${detailPageTitle}`} </h1>
            </div>
            <div styleName="sub-headline clamped-text" ref={subheadlineRef}>
              {rankingDescription}
            </div>
            {!readMoreClicked && !isDesktop && (
              <button onClick={expandBio} styleName="read-more">
                {READ_MORE}
              </button>
            )}
          </div>
        </div>
        <div styleName="search-row">
          <form onSubmit={handleSearch} styleName="form-style">
            <div styleName="search">
              <div styleName="input">
                {searchText && searchInputRef?.current?.value !== "" && (
                  <span
                    onClick={() => {
                      setSearchText("");
                      searchInputRef.current.value = "";
                    }}
                  >
                    <SvgIconHandler type="close" height="12px" width="12px" viewBox="0 0 12 12" />
                  </span>
                )}
                <input type="text" name="search" placeholder="Search" ref={searchInputRef} />
              </div>
              <Button customStyleName={"bg-brand-primary"} onClickFunction={handleSearch}>
                {isDesktop ? "Search" : <SearchIcon />}
              </Button>
            </div>
          </form>
          <div styleName="list-grid-toggle">
            <SortDropdown columns={columns} handleSort={handleSort} isDesktop={isDesktop} />
            <SocialShare
              popupText="Share this URL"
              template={(props) =>
                SocialShareTemplate({
                  ...props,
                  onShareClick: (platform) => {
                    return trackRankingInteraction("share_click", platform?.toLowerCase() || "share");
                  },
                })
              }
              fullUrl={encodeURI(fullUrl)}
              title={`${label || top15Label} ${activeYear}`}
              size={18}
            />
          </div>
        </div>
        {restoreColumns.length ? (
          <div styleName="removed-columns">
            <div styleName="title">Restore Columns</div>
            <div styleName="buttons">
              {restoreColumns.map((c, i) => (
                <Button key={i} onClickFunction={handleRestoreColumn(c)}>
                  <span>{c.name} </span>
                  <SvgIconHandler type={"arrow"} width={24} height={24} viewBox="0 0 24 24" />
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {showEntities.length < 1 ? (
          <div styleName="oops-text">
            <span>
              {NO_RESULT_FOUND}'{searchText}' <br /> {TRY_AGAIN}
            </span>
          </div>
        ) : (
          <div styleName={`page-body ${restoreColumns.length ? "" : "negative-margin-top"}`}>
            <TableView
              snapshotByRank={showEntities}
              visibleItemsForList={visibleItemsForList}
              rankingEntitySlug={rankingEntitySlug}
              columns={columns}
              setColumns={setColumns}
              onItemClick={(name, position) =>
                trackRankingInteraction(eventActions?.[rankingEntitySlug], name, position)
              }
              onColumnRemove={(columnName) => trackRankingInteraction("column_removed", columnName.toLowerCase())}
            />
          </div>
        )}
        {enableLoadMore && searchCount > 16 && (
          <div styleName="load-more-button">
            <Button onClickFunction={() => getMoreStories()}>{LOAD_MORE}</Button>
          </div>
        )}
        {footnotes?.length > 0 && (
          <div styleName="footnotes">
            <strong>Footnotes:</strong>
            <ul>
              {footnotes.map((note, index) => (
                <li key={index}>{note}</li>
              ))}
            </ul>
          </div>
        )}
        {!isAdfreePage && (
          <div styleName="entity-detail-page-ad-wrapper">
            <ListingAdSlot
              adType="above_footer"
              pageType={label === "Fortune 500 India" ? "fortune-500" : "the-next-500"}
              adStyleName="ad-slot-size-728x90-300x250"
              index={3}
            />
          </div>
        )}
      </div>
    </div>
  );
};

RankingsTable.propTypes = {
  data: PropTypes.object,
};
